import { LockKeyhole } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../actions/button";
import { Alert } from "../components/arc/alert/alert";
import { EmptyState } from "../components/arc/empty-state/empty-state";
import { Skeleton } from "../components/arc/skeleton/skeleton";
import styles from "./shell.module.css";

export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  /** At most one primary action, plus secondary ones (the shell spec). */
  actions?: ReactNode;
}

/** The page's title (Geist 28), one line under it, and its actions (Page Header, `vhu2q`). */
export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div className={styles.pageHeader}>
      <div className={styles.pageHeading}>
        <h1 className={styles.pageTitle}>{title}</h1>
        {description ? <p className={styles.pageDescription}>{description}</p> : null}
      </div>
      {actions ? <div className={styles.pageActions}>{actions}</div> : null}
    </div>
  );
}

/** While a page's data loads. */
export function PageLoading({ label = "Loading" }: { label?: string }) {
  return (
    <div className={styles.state}>
      <Skeleton label={label} lines={6} />
    </div>
  );
}

/** Nothing to show yet, with what to do about it. */
export function PageEmpty({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={styles.state}>
      <EmptyState title={title} description={description} action={action} icon={icon} />
    </div>
  );
}

/** A load failed: what happened and a retry. */
export function PageError({
  title = "This page didn't load",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className={styles.state}>
      <Alert tone="danger" title={title}>
        {message}
      </Alert>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/** The role can't use this page: in place of the page, never a crash or a toast. */
export function NoAccess({
  description = "Your role doesn't include this page. Ask an administrator if you need it.",
}: {
  description?: string;
}) {
  return <PageEmpty title="No access" description={description} icon={<LockKeyhole />} />;
}
